import React, { useState, useEffect } from 'react';
import axios from 'axios';
import config from '../config/config';
import './AccountPage.css';
import { useHistory } from 'react-router-dom';

const AccountPage = ({ match }) => {
  const [activeTab, setActiveTab] = useState('general');
  const [account, setAccount] = useState({});
  const [users, setUsers] = useState([]);
  const [galleries, setGalleries] = useState([]);
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [expandedGalleryId, setExpandedGalleryId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const history = useHistory();

  useEffect(() => {
    const fetchAccountData = async () => {
      try {
        const token = localStorage.getItem('superAdminToken');
        if (!token) {
          history.push('/login');
          return;
        }
        setIsLoading(true);
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        const [accountResponse, usersResponse, galleriesResponse] = await Promise.all([
          axios.get(`${config.serverUrl}/superadmin/account/${match.params.accountId}`),
          axios.get(`${config.serverUrl}/superadmin/account/${match.params.accountId}/users`),
          axios.get(`${config.serverUrl}/superadmin/account/${match.params.accountId}/galleries`)
        ]);

        setAccount(accountResponse.data);
        setUsers(usersResponse.data);
        setGalleries(galleriesResponse.data);
      } catch (error) {
        console.error('Failed to fetch account data:', error);
        if (error.response?.status === 401) {
          localStorage.removeItem('superAdminToken');
          history.push('/login');
        }
      } finally {
        setIsLoading(false);
      }
    };
    fetchAccountData();
  }, [match.params.accountId, history]);

  const toggleUserDetails = (userId) => {
    setExpandedUserId(expandedUserId === userId ? null : userId);
  };

  const getGalleryLink = (urlPrefix) => {
    return `https://${account.domainName}/${urlPrefix}`;
  }

  const toggleGalleryDetails = (galleryId) => {
    setExpandedGalleryId(expandedGalleryId === galleryId ? null : galleryId);
  };

  const handleBackClick = () => {
    history.goBack();
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return (
          <div>
            <p><strong>Account Name:</strong> {account.accountName}</p>
            <p><strong>Domain Name:</strong> {account.domainName}</p>
            <p><strong>Default Language:</strong> {account.defaultLanguage}</p>
          </div>
        );
      case 'backofficeUsers':
        return (
          <div>
            <h2>Backoffice Users</h2>
            <ul>
              {users.map(user => (
                <li key={user.id} onClick={() => toggleUserDetails(user.id)}>
                  {user.email}
                  {expandedUserId === user.id && (
                    <div className="user-details">
                      <p><strong>Email:</strong> {user.email}</p>
                      <p><strong>Created At:</strong> {new Date(user.createdAt).toLocaleDateString()}</p>
                      <p><strong>Verified:</strong> {user.isVerified ? 'Yes' : 'No'}</p>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        );
      case 'galleries':
        return (
          <div>
            <h2>Galleries</h2>
            <ul className="gallery-list">
              {galleries.map(gallery => (
                <li className="gallery-item" key={gallery.id} onClick={() => toggleGalleryDetails(gallery.id)}>
                  <div className="gallery-item-header">
                    <span className="gallery-name">{gallery.name}</span>
                    <span className="photo-count">{gallery.photoCount} photos</span>
                  </div>
                  {expandedGalleryId === gallery.id && (
                    <div className="gallery-details">
                      <p><strong>Description:</strong> {gallery.name}</p>
                      <p><strong>Gallery Date:</strong> {new Date(gallery.galleryDate).toLocaleDateString()}</p>
                      <p><strong>Color Theme:</strong> {gallery.colorTheme}</p>
                      <p><strong>Gallery Language:</strong> {gallery.galleryLanguage}</p>
                      <p><strong>Gallery Category:</strong> {gallery.galleryCategory}</p>
                      <p><strong>Photos Count:</strong> {gallery.photosCount}</p>
                      <p><strong>Total Size:</strong> {gallery.totalSize}</p>
                      <p><strong>Categories:</strong> {gallery.categories.map((category, index) => (
                        <span key={category.id}>
                          {category.name} ({category.photoCount})
                          {index < gallery.categories.length - 1 && ', '}
                        </span>
                      ))}</p>                      
                      <p><strong>Gallery Links:</strong> 
                      
                      {gallery.galleryLinks.map((link, index) => (
                        <div className="gallery-links" onClick={(e) => e.stopPropagation()}>
                        <span key={index}>
                          <br/>{link.name}: <a 
                            href={getGalleryLink(link.urlprefix)} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {getGalleryLink(link.urlprefix)}
                          </a> [{link.Password}]
                        </span> 
                        </div>
                      ))}

                      </p>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        );
      default:
        return null;
    }
  };

  if (isLoading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="account-page-container">
      <button className="back-button" onClick={handleBackClick}>Back</button>
      <div className="account-header">
        <h1>{account.accountName}</h1>
        <p className="account-domain">{account.domainName}</p>
      </div>

      <div className="tabs">
        <button 
          className={`tab-button ${activeTab === 'general' ? 'active' : ''}`}
          onClick={() => setActiveTab('general')}
        >
          General Settings
        </button>
        <button 
          className={`tab-button ${activeTab === 'backofficeUsers' ? 'active' : ''}`}
          onClick={() => setActiveTab('backofficeUsers')}
        >
          Backoffice Users
        </button>
        <button 
          className={`tab-button ${activeTab === 'galleries' ? 'active' : ''}`}
          onClick={() => setActiveTab('galleries')}
        >
          Galleries
        </button>
      </div>

      <div className="tab-content">
        {renderTabContent()}
      </div>
    </div>
  );
};

export default AccountPage;